/**
 * Fuzzy matching utility for the Global Search / Command Palette (#419).
 *
 * Dependency-free (no fuse.js/match-sorter in package.json) scorer used to
 * rank navigation items, commands, and federated entity results against a
 * single free-text query, tolerant of typos/partial input.
 *
 * Scoring, highest to lowest:
 *  1. Exact match (case-insensitive)                    -> 1000+
 *  2. Prefix match ("proye" -> "Proyectos")              -> 500-599
 *  3. Substring match, bonus if it starts on a word boundary -> 250-350
 *  4. Fuzzy subsequence match ("pyto" -> "Proyecto")      -> small positive score
 *  5. No match (not even a subsequence)                   -> null
 *
 * @module utils/fuzzyMatch
 */

/**
 * Score how well `query` matches `text`. Returns `null` when there is no
 * match at all (not even a loose subsequence) so callers can `.filter(Boolean)`.
 *
 * @param {string} query
 * @param {string} text
 * @returns {number|null}
 */
export const fuzzyScore = (query, text) => {
  const q = (query || '').trim().toLowerCase();
  const t = (text || '').toLowerCase();

  if (!q) return 0; // empty query matches everything with neutral score
  if (!t) return null;

  if (t === q) return 1000;

  if (t.startsWith(q)) {
    // Shorter target = tighter match = higher score
    return 550 - Math.min(t.length, 50);
  }

  const idx = t.indexOf(q);
  if (idx !== -1) {
    const prevChar = t[idx - 1];
    const onWordBoundary = idx === 0 || /[\s\-_/.,]/.test(prevChar);
    return 300 + (onWordBoundary ? 30 : 0) - Math.min(idx, 25);
  }

  // Fuzzy subsequence fallback: every character of q must appear in t, in
  // order, but not necessarily contiguous ("pyto" matches "Proyecto").
  let qi = 0;
  let score = 0;
  let lastMatchIdx = -1;
  let consecutiveRun = 0;

  for (let ti = 0; ti < t.length && qi < q.length; ti += 1) {
    if (t[ti] === q[qi]) {
      consecutiveRun = lastMatchIdx === ti - 1 ? consecutiveRun + 1 : 0;
      score += 10 + consecutiveRun * 4;
      lastMatchIdx = ti;
      qi += 1;
    }
  }

  if (qi < q.length) return null; // query has characters that never matched

  // Slight penalty for longer haystacks so tighter matches rank first.
  return Math.max(1, score - t.length * 0.15);
};

/**
 * Boolean convenience wrapper around {@link fuzzyScore}.
 * @param {string} query
 * @param {string} text
 * @returns {boolean}
 */
export const fuzzyMatch = (query, text) => fuzzyScore(query, text) !== null;

/**
 * Score an item against multiple candidate fields (e.g. title + subtitle),
 * returning the best score found, or `null` if none of the fields match.
 *
 * @param {string} query
 * @param {Array<string|undefined|null>} fields
 * @returns {number|null}
 */
export const fuzzyScoreFields = (query, fields = []) => {
  let best = null;
  fields.forEach((field) => {
    const score = fuzzyScore(query, field);
    if (score !== null && (best === null || score > best)) {
      best = score;
    }
  });
  return best;
};

/**
 * Filter + rank a list of arbitrary items against a query.
 *
 * @template T
 * @param {string} query
 * @param {T[]} items
 * @param {(item: T) => Array<string|undefined|null>} getFields - fields to match on, best score wins
 * @returns {Array<{ item: T, score: number }>} sorted descending by score
 */
export const rankItems = (query, items = [], getFields) => {
  const results = [];
  items.forEach((item) => {
    const score = fuzzyScoreFields(query, getFields(item));
    if (score !== null) {
      results.push({ item, score });
    }
  });
  results.sort((a, b) => b.score - a.score);
  return results;
};

const fuzzyMatchUtils = { fuzzyScore, fuzzyMatch, fuzzyScoreFields, rankItems };
export default fuzzyMatchUtils;
